import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getBlogPost, getRelatedPosts, type BlogSection, type InlineLink } from '../../data/blogPosts';
import { getBlogAction } from '../../data/blogActions';
import { updateBlogPostMetadata } from '../../utils/seo';
import { Clock, ArrowLeft, ArrowRight } from '../../components/icons';
import AdBanner from '../../components/AdBanner';
import ContrastExample from '../../components/ContrastExample';
import ColorDemoBlock from '../../components/ColorDemoBlock';
import AuthorBio from '../../components/AuthorBio';
import Layout from '../../components/Layout';
import NotFoundPage from '../NotFoundPage';

const AD_SLOT_IN_ARTICLE = '8843219021';

/** Replace plain-text link anchors with <a> elements */
const renderTextWithLinks = (text: string, links?: InlineLink[]): React.ReactNode => {
  if (!links || links.length === 0) return text;

  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIdx = 0;

  for (const link of links) {
    const idx = remaining.indexOf(link.text);
    if (idx === -1) continue;

    if (idx > 0) parts.push(remaining.slice(0, idx));
    parts.push(
      <Link key={keyIdx++} to={link.href} className="text-brand-600 hover:text-brand-700 underline underline-offset-2">
        {link.text}
      </Link>
    );
    remaining = remaining.slice(idx + link.text.length);
  }

  if (remaining) parts.push(remaining);
  return parts.length > 0 ? parts : text;
};

const sectionId = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const SectionRenderer: React.FC<{ section: BlogSection }> = ({ section }) => {
  switch (section.type) {
    case 'heading':
      return <h2 id={sectionId(section.content)} className="text-xl font-bold text-ink mt-8 mb-3 scroll-mt-20">{section.content}</h2>;
    case 'code':
      return <pre className="bg-zinc-900 text-zinc-100 rounded-xl p-5 my-5 overflow-x-auto text-sm leading-relaxed"><code>{section.content}</code></pre>;
    case 'paragraph':
      return <p className="text-ink-secondary leading-relaxed mb-4">{renderTextWithLinks(section.content, section.links)}</p>;
    case 'list':
      return (
        <ul className="mb-4 space-y-2">
          {(section.items ?? []).map((item, i) => (
            <li key={i} className="flex gap-2 text-ink-secondary leading-relaxed">
              <span className="text-brand-500 font-bold flex-shrink-0 mt-0.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'callout':
      return (
        <div className="bg-brand-50 border-l-4 border-brand-400 rounded-r-lg p-4 my-6">
          <p className="text-brand-800 text-sm leading-relaxed">{section.content}</p>
        </div>
      );
    case 'contrast-case':
      return section.fg && section.bg ? <ContrastExample fg={section.fg} bg={section.bg} caption={section.content} /> : null;
    case 'color-demo':
      return (
        <ColorDemoBlock
          colors={section.colors ?? []}
          caption={section.content || undefined}
          contrastInfo={section.contrastInfo}
          layout={section.colors?.length === 2 ? 'comparison' : 'row'}
        />
      );
    default:
      return null;
  }
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;
  const related = slug ? getRelatedPosts(slug, 3) : [];

  useEffect(() => {
    if (!post) return;
    updateBlogPostMetadata({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      updated: post.updated,
      sources: post.sources,
      author: post.author,
      slug: post.slug,
      tags: post.tags,
      readingTime: post.readingTime,
    });
    return () => {
      document.getElementById('blog-post-jsonld')?.remove();
    };
  }, [post]);

  if (!post) return <NotFoundPage />;

  const midIndex = post.sections.length > 5 ? 3 : -1;
  const action = getBlogAction(post.slug);

  return (
    <Layout maxWidth="max-w-3xl">
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-brand-600 transition-colors mb-8">
        <ArrowLeft size={14} />
        Back to Blog
      </Link>

      <article>
        <header className="mb-8">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {post.tags.map(tag => (
              <span
                key={tag}
                className="ui-badge"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="page-title mb-4">{post.title}</h1>
          <p className="text-ink-secondary text-lg leading-relaxed mb-4">{post.excerpt}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink-muted border-t border-surface-border pt-4">
            <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Clock size={13} /> {post.readingTime} min read</span>
            <span>·</span>
            <span>{post.author}</span>
          </div>
        </header>

{post.updated && <p className="text-sm text-ink-secondary mb-3">Updated <time dateTime={post.updated}>{post.updated}</time>: {post.revision}</p>}
        <nav aria-label="On this page" className="ui-panel p-5 mb-8"><p className="font-semibold mb-3">On this page</p><ul className="space-y-2 text-sm">{post.sections.filter(s=>s.type==='heading').map(s=><li key={s.content}><a className="text-brand-600 underline" href={'#'+sectionId(s.content)}>{s.content}</a></li>)}</ul></nav>

        <div className="ui-prose max-w-none">
          {post.sections.map((section, index) => (
            <React.Fragment key={index}>
              <SectionRenderer section={section} />
              {index === midIndex && (
                <AdBanner slotId={AD_SLOT_IN_ARTICLE} format="fluid" className="my-6" />
              )}
            </React.Fragment>
          ))}
        </div>
{post.sources && <section className="mt-8 pt-6 border-t border-surface-border"><h2 className="text-xl font-bold mb-3">Sources and further reading</h2><ul className="list-disc pl-5 space-y-2 text-sm">{post.sources.map(source=><li key={source.href}><a className="text-brand-600 underline" href={source.href}>{source.text}</a></li>)}</ul><p className="text-sm mt-4"><Link className="text-brand-600 underline" to="/editorial-policy">Editorial and correction policy</Link></p></section>}
      </article>

      {action && <div className="ui-panel mt-12 p-6">
        <p className="text-sm text-ink-secondary mb-3">Try it yourself</p>
        <h2 className="text-lg font-bold text-ink mb-2">{action.title}</h2>
        <p className="text-ink-secondary text-sm mb-4">{action.description}</p>
        <Link to={action.href} className="ui-button-primary">
          {action.label} <ArrowRight size={16} />
        </Link>
      </div>}

      <AuthorBio />

      <AdBanner slotId={AD_SLOT_IN_ARTICLE} format="horizontal" className="mt-8" />

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-lg font-bold text-ink mb-4">Related Articles</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {related.map(relatedPost => (
              <Link
                key={relatedPost.slug}
                to={`/blog/${relatedPost.slug}`}
                className="ui-panel p-4 hover:border-brand-300 hover:shadow-sm transition-all"
              >
                <div className="flex flex-wrap gap-1 mb-2">
                  {relatedPost.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="ui-badge">{tag}</span>
                  ))}
                </div>
                <p className="text-ink-secondary font-medium text-sm leading-snug">{relatedPost.title}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
};

export default BlogPostPage;
